import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  type CSSProperties,
} from 'react';
import { HiChevronLeft } from 'react-icons/hi';
import { BsGrid3X3 } from 'react-icons/bs';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, Button, Input, toasts } from '../../../../../ui-component';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import type { StitchOptionApi } from './optionsModalTypes';
import StitchOptionIconSelectorModal, {
  type StitchOptionIconType,
} from './StitchOptionIconSelectorModal';
import defaultOptionIcon from '../../../../../assets/images/image 354.png';

type MultiSelectOptionModalProps = {
  atomicOutfitId: number | null;
  existingOption: StitchOptionApi | null;
  onSaved: () => void;
  onClose: () => void;
};

type OptionRow = {
  name: string;
  price: number;
  image_ref_id: string;
  image_url?: string;
};

type SortableOptionRowProps = {
  index: number;
  option: OptionRow;
  disabled: boolean;
  onIconClick: (index: number) => void;
  onNameChange: (index: number, value: string) => void;
  onPriceChange: (index: number, value: number) => void;
  onRemove: (index: number) => void;
};

const SortableOptionRow = ({
  index,
  option,
  disabled,
  onIconClick,
  onNameChange,
  onPriceChange,
  onRemove,
}: SortableOptionRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  const baseClass = 'flex items-center gap-2 mb-3 rounded-lg border border-transparent';
  const dragClass = isDragging ? 'bg-blue-50 border-blue-400' : 'bg-transparent';
  const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${baseClass} ${dragClass} ${cursorClass}`}
    >
      <span className="font-inter text-sm text-gray-500 w-6">{index + 1}.</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onIconClick(index);
        }}
        className="w-12 h-12 rounded-lg bg-blue-50 border border-dashed border-blue-200 flex items-center justify-center flex-shrink-0 overflow-hidden hover:border-blue-500 hover:bg-blue-100 transition-colors"
        aria-label="Select icon"
      >
        <img
          src={option.image_url || defaultOptionIcon}
          alt="Option icon"
          className="w-full h-full object-contain"
        />
      </button>
      <Input
        label=""
        type="text"
        value={option.name}
        onChange={(e) => onNameChange(index, e.target.value)}
        placeholder="Option name"
        className="flex-1"
      />
      <input
        type="number"
        value={option.price === 0 ? '' : option.price}
        onChange={(e) => onPriceChange(index, e.target.value ? Number(e.target.value) : 0)}
        placeholder="₹ Price"
        className="w-24 font-inter border border-gray-300 rounded px-2 py-2 text-sm"
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
        aria-label="Remove option"
      >
        <MdDelete size={18} />
      </button>
    </div>
  );
};

const MultiSelectOptionModal = forwardRef<ModalMethods, MultiSelectOptionModalProps>(
  ({ atomicOutfitId, existingOption, onSaved, onClose }, ref) => {
    const modalRef = useRef<ModalMethods>(null);
    const iconSelectorModalRef = useRef<ModalMethods>(null);
    const [iconRowIndex, setIconRowIndex] = useState<number | null>(null);
    const [fieldLabel, setFieldLabel] = useState('');
    const [options, setOptions] = useState<OptionRow[]>([
      { name: '', price: 0, image_ref_id: '', image_url: defaultOptionIcon },
    ]);
    const [isSaving, setIsSaving] = useState(false);
    const [isReordering, setIsReordering] = useState(false);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      })
    );

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      toggle: () => modalRef.current?.toggle(),
      getModalState: () => modalRef.current?.getModalState() ?? false,
    }));

    useEffect(() => {
      if (existingOption) {
        setFieldLabel(existingOption.key);
        setOptions(
          existingOption.values.length > 0
            ? existingOption.values.map((v) => ({
                name: v.field_label,
                price: Number(v.price) || 0,
                image_ref_id: v.ref_id ?? '',
                image_url: v.image_url || defaultOptionIcon,
              }))
            : [{ name: '', price: 0, image_ref_id: '', image_url: defaultOptionIcon }]
        );
      } else {
        setFieldLabel('');
        setOptions([{ name: '', price: 0, image_ref_id: '', image_url: defaultOptionIcon }]);
      }
    }, [existingOption]);

    const addOption = () => {
      setOptions((prev) => [
        ...prev,
        { name: '', price: 0, image_ref_id: '', image_url: defaultOptionIcon },
      ]);
    };

    const removeOption = (index: number) => {
      setOptions((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
    };

    const updateOption = (index: number, field: keyof OptionRow, value: string | number) => {
      setOptions((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const handleIconClick = (index: number) => {
      setIconRowIndex(index);
      iconSelectorModalRef.current?.show();
    };

    const handleIconSelect = (icon: StitchOptionIconType) => {
      if (iconRowIndex === null) {
        return;
      }

      const referenceId =
        icon.reference_id ||
        (icon.image_url ? icon.image_url.split('/').pop() || '' : '') ||
        (icon.id ? String(icon.id) : '');

      const imageUrl = icon.image_url || icon.short_lived_url || defaultOptionIcon;

      setOptions((prev) =>
        prev.map((row, i) =>
          i === iconRowIndex ? { ...row, image_ref_id: referenceId, image_url: imageUrl } : row
        )
      );

      setIconRowIndex(null);
    };

    const persistOptionOrder = useCallback(
      async (orderedOptions: OptionRow[]) => {
        if (!existingOption || !atomicOutfitId) {
          return;
        }

        const boutique_id = getValueFromLocalStorage('boutique_id');
        const trimmedLabel = fieldLabel.trim();

        if (!boutique_id || !trimmedLabel) {
          return;
        }

        const validOptions = orderedOptions.filter((o) => o.name.trim() !== '');

        if (validOptions.length === 0) {
          return;
        }

        setIsReordering(true);

        try {
          const payload = {
            atomic_outfit_id: atomicOutfitId,
            multi_select_stitch_option_request: {
              field_label: trimmedLabel,
              field_type: 2,
              options: validOptions.map((o) => ({
                name: o.name.trim(),
                price: Number(o.price) || 0,
                image_ref_id: o.image_ref_id.trim() || 'string',
                ref_id: o.image_ref_id.trim() || null,
              })),
            },
          };

          const response = await api.putRequest(
            `new-stitch-option/stitch-options/${existingOption.id}/v2`,
            payload
          );

          if (!response.status) {
            toasts(
              'error',
              response.message || 'Failed to update option order',
              'multi-select-order-error'
            );
          } else {
            // keep modal open; just sync local state shape to payload
            setOptions(
              payload.multi_select_stitch_option_request.options.map((o) => ({
                name: o.name,
                price: o.price,
                image_ref_id: (o as { ref_id?: string | null }).ref_id ?? o.image_ref_id ?? '',
                image_url:
                  options.find((opt) => opt.name.trim() === o.name.trim())?.image_url ||
                  defaultOptionIcon,
              }))
            );
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'multi-select-order-error');
          }
        } finally {
          setIsReordering(false);
        }
      },
      [atomicOutfitId, existingOption, fieldLabel, options]
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
          return;
        }

        setOptions((prev) => {
          const oldIndex = Number(active.id);
          const newIndex = Number(over.id);

          if (
            Number.isNaN(oldIndex) ||
            Number.isNaN(newIndex) ||
            oldIndex < 0 ||
            newIndex < 0 ||
            oldIndex >= prev.length ||
            newIndex >= prev.length
          ) {
            return prev;
          }

          const newOptions = arrayMove(prev, oldIndex, newIndex);

          void persistOptionOrder(newOptions);

          return newOptions;
        });
      },
      [persistOptionOrder]
    );

    const handleSave = async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !atomicOutfitId) {
        toasts('error', 'Boutique or piece not selected', 'multi-select-save-error');

        return;
      }

      const trimmedLabel = fieldLabel.trim();

      if (!trimmedLabel) {
        toasts('error', 'Enter field label', 'multi-select-label-error');

        return;
      }

      const validOptions = options.filter((o) => o.name.trim() !== '');

      if (validOptions.length === 0) {
        toasts('error', 'Add at least one option', 'multi-select-options-error');

        return;
      }

      setIsSaving(true);
      try {
        const payload = {
          atomic_outfit_id: atomicOutfitId,
          multi_select_stitch_option_request: {
            field_label: trimmedLabel,
            field_type: 2,
            options: validOptions.map((o) => ({
              name: o.name.trim(),
              price: Number(o.price) || 0,
              image_ref_id: o.image_ref_id.trim() || 'string',
              ref_id: o.image_ref_id.trim() || null,
            })),
          },
        };

        if (existingOption) {
          const response = await api.putRequest(
            `new-stitch-option/stitch-options/${existingOption.id}/v2`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option updated successfully', 'multi-select-update-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts(
              'error',
              response.message || 'Failed to update option',
              'multi-select-update-error'
            );
          }
        } else {
          const response = await api.postRequest(
            `new-stitch-option/${boutique_id}/boutique_stitch_options`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option added successfully', 'multi-select-add-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts('error', response.message || 'Failed to add option', 'multi-select-add-error');
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'multi-select-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            modalRef.current?.hide();
            onClose();
          }}
          className="p-1 rounded hover:bg-gray-100 flex items-center gap-1"
          aria-label="Back"
        >
          <HiChevronLeft size={24} />
          <span className="font-inter text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <BsGrid3X3 size={20} className="text-gray-600" />
          <span className="font-inter font-semibold text-lg">Multi select option</span>
        </div>
      </div>
    );

    const footerComponent = (
      <Button
        label="Save"
        size="medium"
        bgColor="primary"
        onClick={handleSave}
        disabled={isSaving}
        className="flex items-center gap-2"
      />
    );

    return (
      <>
        <Modal
          ref={modalRef}
          size="xl"
          headerComponent={headerComponent}
          footerComponent={footerComponent}
          showFooter={true}
          showCloseIcon={false}
        >
          <div className="flex flex-col gap-4 py-4">
            <div>
              <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
                Field label
              </label>
              <input
                type="text"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                placeholder="Enter field label"
                className="w-full font-inter border-b border-gray-300 py-2 px-0 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="font-inter text-sm font-medium text-gray-700 block mb-2">
                Options
              </label>
              {options.length > 1 && (
                <p className="font-inter text-xs text-gray-500 mb-2">
                  {isReordering
                    ? 'Reordering disabled while saving order'
                    : 'Drag any option row to re-arrange'}
                </p>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={options.map((_, index) => index)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1">
                    {options.map((row, index) => (
                      <SortableOptionRow
                        key={index}
                        index={index}
                        option={row}
                        disabled={isReordering || isSaving}
                        onIconClick={handleIconClick}
                        onNameChange={(idx, value) => updateOption(idx, 'name', value)}
                        onPriceChange={(idx, value) => updateOption(idx, 'price', value)}
                        onRemove={removeOption}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <Button
                label="Add +"
                size="small"
                bgColor="primary"
                onClick={addOption}
                className="mt-2"
              />
            </div>
          </div>
        </Modal>

        <StitchOptionIconSelectorModal
          ref={iconSelectorModalRef}
          onIconSelect={handleIconSelect}
          selectedIconRefId={
            iconRowIndex !== null ? options[iconRowIndex]?.image_ref_id || undefined : undefined
          }
        />
      </>
    );
  }
);

MultiSelectOptionModal.displayName = 'MultiSelectOptionModal';

export default MultiSelectOptionModal;
